import React, { useCallback, useEffect, useRef } from 'react';
import { MediaAudioProps } from './types';

export type MediaAudioComponentProps = Pick<MediaAudioProps, 'src'> & {
  /** Defaults to `true`. */
  controls?: boolean;
  /** Defaults to `'auto'`. */
  preload?: 'none' | 'metadata' | 'auto';
  /** whether the video will play automatically when loaded, defaults to false */
  autoPlay?: boolean;
  /**
   * When toggled to `false` the component pauses the underlying `<video>`.
   * Default: `true`.
   */
  isVisible?: boolean;
  onPlay?: React.ReactEventHandler<HTMLAudioElement>;
  onPause?: React.ReactEventHandler<HTMLAudioElement>;
  onEnded?: React.ReactEventHandler<HTMLAudioElement>;
  /** Sets the `data-qa-id` attribute (Playwright selector). */
  'data-qa-id'?: string;
  /** Sets the `data-testid` attribute (Jest selector). */
  'data-testid'?: string;
};

/**
 * `MediaAudio` — pure presentational `<audio>` element driven by a URL.
 *
 * Caller is responsible for any wrapping affordances (download buttons,
 * lock banners, etc.). `onPlay` / `onPause` / `onEnded` are wired to the
 * native events.
 */
export const MediaAudio = React.forwardRef<HTMLAudioElement, MediaAudioComponentProps>(
  (
    {
      src,
      controls = true,
      preload = 'metadata',
      autoPlay = false,
      isVisible = true,
      onPlay,
      onPause,
      onEnded,
      'data-qa-id': qaId,
      'data-testid': testId,
    },
    forwardedRef
  ) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Keep an internal ref for component behavior while also supporting an
    // optional consumer ref (object or callback) that points to the same node.
    const setAudioRef = useCallback(
      (node: HTMLAudioElement | null) => {
        const consumerRef = forwardedRef;

        audioRef.current = node;

        if (typeof consumerRef === 'function') {
          consumerRef(node);
        } else if (consumerRef) {
          consumerRef.current = node;
        }
      },
      [forwardedRef]
    );

    // This effect always uses the internal ref so visibility behavior works
    // regardless of whether a consumer passes a ref.
    useEffect(() => {
      if (!isVisible) {
        audioRef.current?.pause();
      }
    }, [isVisible]);

    return (
      // Timed-text assets are feature-specific; consumers must pair audio-only
      // media with an accessible transcript or equivalent nearby text.
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <audio
        ref={setAudioRef}
        src={src}
        controls={controls}
        controlsList="nodownload"
        preload={preload}
        autoPlay={autoPlay}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        {...(qaId ? { 'data-qa-id': qaId } : {})}
        {...(testId ? { 'data-testid': testId } : {})}
        className="cn_max-w-full cn_w-full "
      />
    );
  }
);

MediaAudio.displayName = 'MediaAudio';
